import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload, apiUploadPut } from '../utils/api';

const useApi = (url, options = {}) => {
  const {
    fetchOnMount = true,
    initialData = null,
    params = {},
    onSuccess = null,
    onError = null,
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch data
  const fetchData = useCallback(async (customParams = {}) => {
    setLoading(true);
    setError(null);

    try {
      const mergedParams = { ...params, ...customParams };
      console.log(`Fetching data from ${url} with params:`, mergedParams);

      const response = await apiGet(url, mergedParams);
      console.log(`API Response for ${url}:`, response);

      // Handle different response formats
      // Some endpoints return { hotels: [...], count: X }
      // Others return directly the array of items
      if (response && Array.isArray(response)) {
        console.log(`Response is an array with ${response.length} items`);
        setData(response);
      } else if (response && typeof response === 'object') {
        console.log('Response is an object with keys:', Object.keys(response));
        setData(response);
      } else {
        console.log('Response is in an unexpected format:', response);
        setData(response || []);
      }

      if (onSuccess) {
        onSuccess(response);
      }

      return response;
    } catch (err) {
      console.error(`Error fetching data from ${url}:`, err);
      setError(err);

      if (onError) {
        onError(err);
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [url, params, onSuccess, onError]);

  // Create data
  const createData = useCallback(async (payload) => {
    setLoading(true);
    setError(null);

    try {
      // Check if payload is FormData
      const isFormData = payload instanceof FormData;

      // For destinations, we need to use the create endpoint
      const endpoint = isFormData ? `${url}create/` : url;

      console.log('Creating data at endpoint:', endpoint);

      // Use apiUpload for FormData, otherwise use apiPost
      const response = isFormData
        ? await apiUpload(endpoint, payload)
        : await apiPost(url, payload);

      if (onSuccess) {
        onSuccess(response);
      }

      return response;
    } catch (err) {
      console.error('Error in createData:', err);
      setError(err);

      if (onError) {
        onError(err);
      }

      // Re-throw the error so it can be caught by the component
      throw err;
    } finally {
      setLoading(false);
    }
  }, [url, onSuccess, onError]);

  // Update data
  const updateData = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);

    try {
      // Check if the ID is a slug (string) or a numeric ID
      const isSlug = typeof id === 'string' && isNaN(parseInt(id));

      // For destinations, we need to construct the correct URL
      // The URL should be like: /destinations/thalande/update/
      // But we're already at /destinations/thalande/ from the useApi hook initialization
      const endpoint = isSlug ? `${url.replace(`${id}/`, '')}${id}/update/` : `${url}${id}/`;

      console.log('Update endpoint:', endpoint);
      console.log('Update payload:', payload);

      // Log FormData contents if applicable
      if (payload instanceof FormData) {
        console.log('FormData contents:');
        for (let pair of payload.entries()) {
          console.log(`  ${pair[0]}: ${typeof pair[1] === 'object' ? 'File or Object' : pair[1]}`);
        }
      }

      // Check if payload is FormData
      const isFormData = payload instanceof FormData;

      // Use apiUploadPut for FormData with PUT method, otherwise use apiPut
      const response = isFormData
        ? await apiUploadPut(endpoint, payload)
        : await apiPut(endpoint, payload);

      console.log('Update response:', response);

      if (onSuccess) {
        onSuccess(response);
      }

      return response;
    } catch (err) {
      console.error('Error in updateData:', err);
      setError(err);

      if (onError) {
        onError(err);
      }

      // Re-throw the error so it can be caught by the component
      throw err;
    } finally {
      setLoading(false);
    }
  }, [url, onSuccess, onError]);

  // Delete data
  const deleteData = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      // Check if the ID is a slug (string) or a numeric ID
      const isSlug = typeof id === 'string' && isNaN(parseInt(id));

      // Determine the correct endpoint based on the URL and ID type
      let endpoint;

      // Special case for bookings - always use the /delete/ endpoint
      if (url.includes('bookings')) {
        endpoint = `${url}${id}/delete/`;
      } else {
        // For destinations and other resources with slugs, use the slug directly
        endpoint = isSlug ? `${url}${id}/delete/` : `${url}${id}/`;
      }

      console.log('Delete endpoint:', endpoint);

      const response = await apiDelete(endpoint);

      if (onSuccess) {
        onSuccess(response);
      }

      return response;
    } catch (err) {
      console.error('Error in deleteData:', err);
      setError(err);

      if (onError) {
        onError(err);
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [url, onSuccess, onError]);

  // Fetch data on mount
  useEffect(() => {
    if (fetchOnMount) {
      // Use a ref to prevent multiple calls during development with React.StrictMode
      const controller = new AbortController();
      fetchData();

      return () => {
        controller.abort();
      };
    }
  }, [fetchOnMount]); // Remove fetchData from dependencies to prevent multiple calls

  return {
    data,
    loading,
    error,
    fetchData,
    createData,
    updateData,
    deleteData,
    setData,
  };
};

export default useApi;
